import { DECIDE_MSG, ESTIMATE_FILTER_LABEL, ESTIMATE_PC_MSG, TRASH_MSG, estimateFilterOf, type DecideResult, type EstimateFilter, type EstimateList, type EstimateSummary,
  displayDocumentNo,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { ListHead } from "../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { RowMenu } from "../components/RowMenu.jsx";
import { usePageName } from "../components/BackButton.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, useToast } from "../components/ui.jsx";
import { useLabels } from "../labels.js";
import { useOngoing } from "../lib/ongoing.js";
import { md, yen } from "../lib/num.js";
import { useCreateInvoice } from "./Invoices.jsx";
import { TrashTable, useTrashFlash } from "./estimates/Trash.jsx";

const FILTERS: EstimateFilter[] = ["all", "draft", "waiting", "ordered", "billed", "lost"];
/** #200（docs/81）：タブの最後に「ゴミ箱」（状態の絞り込みではないので「すべて」に入れない） */
type ListKey = EstimateFilter | "trash";

/**
 * 見積書の一覧（試作 v3.9 の③、docs/10 §9.2）。押すとその紙が開く。
 *   ・状態で絞るタブ：すべて／下書き／未受注／受注／請求済／失注（#187 で 返事待ち・受注（未請求）・請求済み から。件数つき、幅はそろえる。9/24 にボタンからタブへ）。ホームの「ひらく」は ?f= で絞って来る
 *   ・未受注の行に「受注」「失注」（確認は挟まず、トーストの「元に戻す」で戻せる。024）
 *   ・受注の行に「請求書をつくる」（見積の明細を写して請求書に）。下書きがあれば「請求書をひらく」
 *   ・タブの最後に「ゴミ箱 n」（#200、docs/81）：削除した見積書（30日。番号のある見積書は残り続ける）と「復元」
 */
export function EstimatesPage() {
  const L = useLabels();
  const og = useOngoing();
  usePageName("見積書");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const createInvoice = useCreateInvoice(toast);
  const [sp, setSp] = useSearchParams();
  const fk = sp.get("f") ?? "";
  const f: ListKey = fk === "trash" ? "trash" : (FILTERS as string[]).includes(fk) ? (fk as EstimateFilter) : "all";
  const clientId = sp.get("client") ?? "";
  const setF = (k: ListKey) => setSp({ ...(k === "all" ? {} : { f: k }), ...(clientId ? { client: clientId } : {}) }, { replace: true });
  useTrashFlash();
  const list = useQuery({ queryKey: ["estimates"], queryFn: () => api<EstimateList>("GET", "/estimates") });
  // 顧客の詳細から来たら、その顧客の見積だけ（028）
  const all = (list.data?.items ?? []).filter((x) => !clientId || x.clientId === clientId);
  const clientName = clientId ? (list.data?.items ?? []).find((x) => x.clientId === clientId)?.clientName : undefined;
  const count = (k: EstimateFilter) => (k === "all" ? all.length : all.filter((x) => estimateFilterOf(x) === k).length);
  const items = f === "all" || f === "trash" ? all : all.filter((x) => estimateFilterOf(x) === f);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["estimates"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    void qc.invalidateQueries({ queryKey: ["estimate"] });
  };
  const undecide = async (x: EstimateSummary) => {
    try {
      await api("POST", `/estimates/${x.id}/undecide`, {});
      toast(`${x.subject || x.clientName} を未受注に戻しました。`);
      refresh();
    } catch (e) {
      toast(e instanceof ApiError && e.code === "UNDECIDE_HAS_INVOICE" ? DECIDE_MSG.hasInvoice : messageOf(e), undefined, "error");
    }
  };
  const decide = async (x: EstimateSummary, decision: "ordered" | "lost") => {
    try {
      await api<DecideResult>("POST", `/estimates/${x.id}/decide`, { decision });
      refresh();
      // 「未受注」で絞っているときは、行が消えて見えなくならないよう、その行の新しい状態へ絞りを移す
      if (f === "waiting") setF(decision === "ordered" ? "ordered" : "lost");
      toast(decision === "ordered" ? `${x.subject || x.clientName} を受注にしました。「請求書作成」から請求書を起こせます。` : `${x.subject || x.clientName} を失注にしました。`, { label: "元に戻す", onClick: () => void undecide(x) });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };

  return (
    <ListHead title="見積書">
      {clientId ? (
        <div className="row" style={{ marginBottom: 8 }}>
          <span className="chip ai">{L.customer}：{clientName ?? "（見積なし）"}</span>
          <button type="button" className="btn text" onClick={() => setSp(f === "all" ? {} : { f }, { replace: true })}>絞り込み解除</button>
          <Link className="btn text" to={`/partners/${clientId}`}>{L.customer}詳細へ</Link>
        </div>
      ) : null}
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className="lcard">
      <ListTabs<ListKey>
        id="el"
        label="見積書を状態で絞る"
        tabs={[
          ...FILTERS.map((k) => ({ key: k, label: ESTIMATE_FILTER_LABEL[k], count: list.data ? count(k) : "…" })),
          { key: "trash" as const, label: TRASH_MSG.tab, count: list.data ? (list.data.trashCount ?? 0) : "…" },
        ]}
        value={f}
        onChange={setF}
        create={
          <Link className="btn primary" to="/estimates/new" data-testid="estimate-new">
            <Icon k="plus" />
            見積書作成
          </Link>
        }
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel("el", f)}>
        {f === "trash" ? <TrashTable /> : (
        <table className="list">
          <thead>
            <tr>
              <th>{L.customer}</th>
              <th>{L.subject}</th>
              <th>日付</th>
              <th className="amt">金額（税込）</th>
              <th>いまの状態</th>
              <th>番号</th>
              <th>返事・請求</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => {
              const st = estimateFilterOf(x);
              return (
                <tr key={x.id} className="r" onClick={() => nav(`/estimates/${x.id}`)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && nav(`/estimates/${x.id}`)}>
                  <td>
                    <b>{x.clientName}</b>{x.branchName ? <span className="muted"> {x.branchName}</span> : null}
                  </td>
                  <td>
                    {x.subject || <span className="muted">（{L.subject}はまだ）</span>}
                    {/* {project}の行（#277、docs/99 §8・docs/28 §4 の改め）：{project}があれば必ず2行目。{project}名＝件名なら名前を繰り返さず
                        「{project}表示 ›」、違えば「{project}：名前 ›」。押すと{project}へ（行の押下＝見積書を開く は起きない） */}
                    {x.groupName ? (
                      <div className="erow-group" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        {x.groupId ? (
                          <Link to={`/groups/${x.groupId}`} data-testid="row-group">
                            {x.groupName === x.subject && !(og.on && x.groupIsOngoing) ? ESTIMATE_PC_MSG.openGroup.replace("{project}", L.project) : `${L.project}：${x.groupName}`} ›
                          </Link>
                        ) : (
                          `${L.project}：${x.groupName}`
                        )}
                      </div>
                    ) : null}
                  </td>
                  <td className="num">{md(x.issueDate)}</td>
                  <td className="amt num">{x.total ? `${yen(x.total)} 円` : <span className="muted">—</span>}</td>
                  <td>
                    {st === "draft" ? <span className="chip">下書き</span> : st === "waiting" ? "未受注" : st === "ordered" ? <span className="chip ai">受注</span> : st === "billed" ? "請求済" : <span className="chip">失注</span>}
                    {x.order.plan ? (
                      <div className="muted" style={{ fontSize: 14 }} data-testid="row-plan">分割 {x.order.plan.issuedCount}／{x.order.plan.count}回 請求済</div>
                    ) : (st === "ordered" || st === "billed") && x.order.invoice ? (
                      <div className="muted" style={{ fontSize: 14 }} data-testid="row-plan">一括</div>
                    ) : null}
                    {x.order.decidedAt ? <div className="muted" style={{ fontSize: 14 }}>{md(x.order.decidedAt.slice(0, 10))}</div> : null}
                  </td>
                  <td className="num">
                    {displayDocumentNo(x.documentNo, x.revisionNo) ?? <span className="muted">未発行</span>}
                    {x.draftRevisionNo != null ? <span className="chip" title={`未発行の ${x.draftRevisionNo}回目があります`}>{x.draftRevisionNo}回目 未発行</span> : null}
                  </td>
                  <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {/* 行の主役の動き1つは枠のボタン、残りは「⋮」のメニュー（#92、components/RowMenu.tsx） */}
                    <div className="row" style={{ gap: 6, flexWrap: "nowrap", justifyContent: "flex-end" }}>
                      {st === "waiting" ? (
                        <button type="button" className="small ai" onClick={() => void decide(x, "ordered")} aria-label={`${x.subject || x.clientName} を受注`}>受注</button>
                      ) : st === "ordered" || st === "billed" ? (
                        // 042：分割の予定があるときは、次の回のボタン（案件の画面と同じ決まり）
                        x.order.plan ? (
                          x.order.plan.next == null ? (
                            <Link className="small" to={`/groups/${x.groupId ?? ""}`}>すべて請求済</Link>
                          ) : x.order.plan.next.draftInvoiceId ? (
                            <Link className="small ai" to={`/invoices/${x.order.plan.next.draftInvoiceId}`}>{x.order.plan.next.seq}回目下書き表示</Link>
                          ) : (
                            <button type="button" className="small ai" onClick={() => void createInvoice(x.id, x.order.plan!.next!.id)}>
                              {x.order.plan.next.seq}回目請求書作成{x.order.plan.next.label ? `（${x.order.plan.next.label}）` : ""}
                            </button>
                          )
                        ) : x.order.invoice && x.order.invoice.status === "draft" ? (
                          <Link className="small ai" to={`/invoices/${x.order.invoice.id}`}>請求書下書き表示</Link>
                        ) : x.order.invoice ? (
                          <Link className="small" to={`/invoices/${x.order.invoice.id}`}>請求書 {x.order.invoice.documentNo}</Link>
                        ) : x.groupBillingTiming === "split" && x.groupId ? (
                          // 042：分割の案件の受注した見積は、まず分割の予定（案件の画面で開く）。一括で請求する道は「⋮」に
                          <Link className="small ai" to={`/groups/${x.groupId}?plan=${x.id}`} data-testid="row-plan-new">分割予定作成</Link>
                        ) : (
                          <button type="button" className="small ai" onClick={() => void createInvoice(x.id)} aria-label={`${x.subject || x.clientName} 請求書作成`}>請求書作成</button>
                        )
                      ) : null}
                      <RowMenu
                        label={`${x.subject || x.clientName} のほかの動き`}
                        testId="row-more"
                        items={[
                          ...(st === "waiting" ? [{ label: "失注", onSelect: () => void decide(x, "lost"), testId: "row-lost" }] : []),
                          ...((st === "ordered" || st === "billed") && !x.order.plan && x.order.invoice && x.order.invoice.status !== "draft" ? [{ label: "請求書追加作成", onSelect: () => void createInvoice(x.id), testId: "row-invoice-more" }] : []),
                          ...((st === "ordered" || st === "billed") && !x.order.plan && !x.order.invoice && x.groupBillingTiming === "split" && x.groupId ? [{ label: "一括請求", onSelect: () => void createInvoice(x.id), testId: "row-invoice-lump" }] : []),
                          ...((st === "ordered" || st === "billed" || st === "lost") && x.order.canUndecide ? [{ label: "未受注に戻す", onSelect: () => void undecide(x), testId: "row-undecide" }] : []),
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={7}>{all.length ? `「${ESTIMATE_FILTER_LABEL[f]}」の見積書はいまありません。` : "まだ見積書はありません。「見積書作成」から作れます。"}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        )}
      </div>
      </div>
      {toastNode}
    </ListHead>
  );
}
